// ===========================================
// The billing notice (roles R17, slice 1b §5.11)
// ===========================================
// A staff member without invoice access who changes a class's weeks, a student's start week, or
// enrols a student into a term the family is already invoiced for gets no invoice list (that is
// finance data). The server says only `invoicesMayNeedAttention`; this tells them, without a
// number, name or amount, so the owner can check. An invoicing user sees the full prompts instead.
//
// The owner is named by the school's own title (roles slice 2, `useOwnerTitle`): "Let your
// Principal know", or "Let your Admin know" until the owner names it.
//
// The notice STAYS until closed (/qa step 5 L4): it arrives after the save's own toasts, and with
// three toasts on screen at most, a timed one could expire before it was ever read.

import { useCallback } from 'react';
import { useSnackbar, type SnackbarKey } from 'notistack';
import { IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useOwnerTitle } from './useOwnerTitle';

export const BILLING_NOTICE = {
  /** A class's first/last week, its chosen weeks, or a student's start week changed. */
  window: (ownerTitle: string) => `Invoices for this class may need changing. Let your ${ownerTitle} know.`,
  /** An enrolment into a term the family was already invoiced for (the dialog's first two sentences). */
  enrol: (ownerTitle: string) =>
    'This family was already invoiced for this term. The next term-invoice run normally bills this class on a ' +
    `separate invoice. If it should be billed sooner, let your ${ownerTitle} know.`,
} as const;

export type BillingNoticeKind = keyof typeof BILLING_NOTICE;

/** `notify(kind, flag)` shows the notice when the server set `invoicesMayNeedAttention`. */
export function useBillingNotice(): (kind: BillingNoticeKind, mayNeedAttention: boolean | undefined) => void {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const ownerTitle = useOwnerTitle();
  return useCallback(
    (kind, mayNeedAttention) => {
      if (!mayNeedAttention) return;
      enqueueSnackbar(BILLING_NOTICE[kind](ownerTitle), {
        variant: 'info',
        persist: true,
        action: (key: SnackbarKey) => (
          <IconButton size="small" color="inherit" aria-label="Close" onClick={() => closeSnackbar(key)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        ),
      });
    },
    [enqueueSnackbar, closeSnackbar, ownerTitle]
  );
}
